import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { PlatformPreviewProps } from '../types';
import { PLATFORM_CONFIG, truncateForPlatform } from '../platformConfig';
import { PreviewAvatar } from '../components/PreviewAvatar';
import { PreviewCaption } from '../components/PreviewCaption';
import { MediaDisplay } from '../components/MediaDisplay';
import { EmptyState } from '../components/EmptyState';

export const LinkedInPreview = ({ caption, media, isCarousel, channel, activeIndex, onIndexChange }: PlatformPreviewProps) => {
  const { t } = useTranslation('common');
  const config = PLATFORM_CONFIG.linkedin;
  const displayCaption = truncateForPlatform(caption, 'linkedin');
  const isEmpty = !caption && media.length === 0;

  const displayName = channel?.displayName ?? '';
  const options = channel?.options;
  const visibility = options?.linkedin?.visibility || 'PUBLIC';
  const firstComment = options?.linkedin?.firstComment;

  if (isEmpty) return <EmptyState />;

  return (
    <div className="bg-white rounded-lg border border-gray-200 w-full min-w-0 max-w-[550px] mx-auto shadow-sm">
      {/* Header */}
      <div className="p-4 flex items-start gap-3">
        <PreviewAvatar colorClass={config.avatarColor} size="lg" avatarUrl={channel?.avatarUrl} initials={channel?.initials} />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 text-sm">{displayName}</p>
          <p className="text-xs text-gray-500 flex items-center gap-1">
            {t('preview_just_now')} <span className="mx-0.5">-</span>
            <Icon name={visibility === 'CONNECTIONS' ? ICONS.USERS : ICONS.GLOBE} size="xs" color="text-gray-500" />
          </p>
        </div>
        <button type="button" className="text-gray-400 hover:text-gray-600 text-xl leading-none">
          ...
        </button>
      </div>

      {/* Caption */}
      {caption && (
        <div className="px-4 pb-3">
          <PreviewCaption
            caption={displayCaption}
            hashtagColor={config.hashtagColor}
            className="text-sm text-gray-900 leading-relaxed"
            truncated={caption.length > 150}
          />
        </div>
      )}

      {/* Media */}
      {media.length > 0 && (
        <div className="border-t border-b border-gray-100">
          <MediaDisplay
            media={isCarousel ? media : media.slice(0, 1)}
            aspectRatio="auto"
            rounded="none"
            showCarouselDots={isCarousel && media.length > 1}
            activeIndex={activeIndex}
            onIndexChange={onIndexChange}
          />
        </div>
      )}

      {/* Action Buttons */}
      <div className="px-1 py-1 flex items-center justify-around border-t border-gray-100">
        <button
          type="button"
          className="flex items-center gap-1 text-gray-600 hover:bg-gray-100 px-1.5 py-1.5 rounded-lg transition-colors min-w-0"
        >
          <Icon name={ICONS.THUMBS_UP} size="xs" className="flex-shrink-0" />
          <span className="text-xs font-medium truncate">{t('preview_li_like')}</span>
        </button>
        <button
          type="button"
          className="flex items-center gap-1 text-gray-600 hover:bg-gray-100 px-1.5 py-1.5 rounded-lg transition-colors min-w-0"
        >
          <Icon name={ICONS.CHAT} size="xs" className="flex-shrink-0" />
          <span className="text-xs font-medium truncate">{t('preview_li_comment')}</span>
        </button>
        <button
          type="button"
          className="flex items-center gap-1 text-gray-600 hover:bg-gray-100 px-1.5 py-1.5 rounded-lg transition-colors min-w-0"
        >
          <Icon name={ICONS.REFRESH} size="xs" className="flex-shrink-0" />
          <span className="text-xs font-medium truncate">{t('preview_li_repost')}</span>
        </button>
        <button
          type="button"
          className="flex items-center gap-1 text-gray-600 hover:bg-gray-100 px-1.5 py-1.5 rounded-lg transition-colors min-w-0"
        >
          <Icon name={ICONS.PAPER_PLANE} size="xs" className="flex-shrink-0" />
          <span className="text-xs font-medium truncate">{t('preview_li_send')}</span>
        </button>
      </div>

      {/* First Comment Preview */}
      {firstComment && (
        <div className="px-4 pb-3 pt-1 border-t border-gray-100">
          <div className="flex items-start gap-2">
            <PreviewAvatar colorClass={config.avatarColor} size="sm" avatarUrl={channel?.avatarUrl} initials={channel?.initials} />
            <div className="flex-1 min-w-0 bg-gray-50 rounded-lg px-3 py-2">
              <p className="text-xs font-semibold text-gray-900">{displayName}</p>
              <p className="text-xs text-gray-700 whitespace-pre-wrap break-words line-clamp-3">{firstComment}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
